import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, X, Rocket, ArrowRight } from "lucide-react";

const STEP_META = {
  activity: { label: "Créer votre première activité", to: "/prestations", hint: "Ex. Quad, Buggy, Jet-ski…" },
  formula: { label: "Ajouter une formule et un tarif", to: "/prestations", hint: "Durée + prix par personne" },
  resource: { label: "Enregistrer une ressource", to: "/flotte", hint: "Vos véhicules ou équipements" },
  team: { label: "Ajouter un membre d'équipe", to: "/equipe", hint: "Guides, chauffeurs, réception" },
  booking: { label: "Créer votre première réservation", to: "/reservations", hint: "Lancez votre activité" },
};

export default function GettingStartedChecklist() {
  const navigate = useNavigate();
  const { orgId, role } = useAuth();
  const canManage = role === "owner" || role === "manager";
  const dismissKey = orgId ? `aktivops.checklist.dismissed.${orgId}` : null;
  const [dismissed, setDismissed] = React.useState(
    () => (dismissKey ? localStorage.getItem(dismissKey) === "1" : false)
  );

  const { data } = useQuery({
    queryKey: ["setup-checklist", orgId],
    queryFn: async () => (await api.get("/api/setup/checklist", { params: { organization_id: orgId } })).data,
    enabled: !!orgId && canManage,
    retry: false,
  });

  if (!canManage || dismissed || !data || data.complete) return null;

  const dismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };
  const pct = Math.round((data.done / data.total) * 100);

  return (
    <div
      data-testid="getting-started-checklist"
      className="mt-6 overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 border-b border-emerald-100 p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white">
            <Rocket className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900">Premiers pas sur ActiPilote</h3>
            <p className="text-sm text-slate-500">
              {data.done} / {data.total} étapes complétées · configurez votre espace en quelques minutes
            </p>
          </div>
        </div>
        <button
          onClick={dismiss}
          data-testid="checklist-dismiss-button"
          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Masquer la checklist"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="h-1.5 w-full bg-emerald-100">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>

      <ul className="divide-y divide-slate-100">
        {data.steps.map((s) => {
          const meta = STEP_META[s.key];
          if (!meta) return null;
          return (
            <li key={s.key} data-testid={`checklist-step-${s.key}`}>
              <button
                onClick={() => navigate(meta.to)}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-emerald-50/60"
              >
                {s.done ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-slate-300" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${s.done ? "text-slate-400 line-through" : "text-slate-800"}`}>
                    {meta.label}
                  </p>
                  {!s.done && <p className="text-xs text-slate-400">{meta.hint}</p>}
                </div>
                {!s.done && <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
